import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
  Grid,
  Chip,
  CircularProgress,
  InputAdornment,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  MenuItem
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { getAllCustomers } from '../services/customerService';

function AdminCustomers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('data');
  const [expandedCustomer, setExpandedCustomer] = useState(null);

  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllCustomers();
      setCustomers(data);
      setFilteredCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    let filtered = customers;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        c.value?.toLowerCase().includes(q)
      );
    }

    if (sortBy === 'interazioni') {
      filtered = [...filtered].sort((a, b) => b.interactionCount - a.interactionCount);
    } else {
      filtered = [...filtered].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    setFilteredCustomers(filtered);
  }, [searchQuery, sortBy, customers]);

  const today = new Date().toDateString();
  const emailCount = customers.filter(c => c.type === 'email').length;
  const phoneCount = customers.filter(c => c.type === 'phone').length;
  const newToday = customers.filter(c => new Date(c.createdAt).toDateString() === today).length;

  const getInteractionIcon = (type) => {
    switch (type) {
      case 'tryon': return <CheckroomIcon fontSize="small" sx={{ color: '#667eea' }} />;
      case 'scan': return <QrCodeScannerIcon fontSize="small" sx={{ color: '#4CAF50' }} />;
      case 'save': return <FavoriteIcon fontSize="small" sx={{ color: '#f44336' }} />;
      default: return <CheckroomIcon fontSize="small" />;
    }
  };

  const getInteractionLabel = (type) => {
    switch (type) {
      case 'tryon': return 'Prova virtuale';
      case 'scan': return 'Scansione';
      case 'save': return 'Salvato';
      default: return type;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <AppBar position="static" sx={{ backgroundColor: '#1a1a1a' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/admin')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Clienti
          </Typography>
          <Chip label={`${customers.length} contatti`} color="primary" />
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Stat Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
              <PeopleIcon sx={{ fontSize: 32, color: '#667eea', mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{customers.length}</Typography>
              <Typography variant="body2" color="text.secondary">Totale Clienti</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
              <PersonAddIcon sx={{ fontSize: 32, color: '#4CAF50', mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{newToday}</Typography>
              <Typography variant="body2" color="text.secondary">Nuovi Oggi</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
              <EmailIcon sx={{ fontSize: 32, color: '#2196F3', mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{emailCount}</Typography>
              <Typography variant="body2" color="text.secondary">Email</Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Paper elevation={2} sx={{ p: 2, textAlign: 'center' }}>
              <PhoneIcon sx={{ fontSize: 32, color: '#FF9800', mb: 0.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{phoneCount}</Typography>
              <Typography variant="body2" color="text.secondary">Telefoni</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Search and Sort */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Cerca per email o telefono..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: '200px', backgroundColor: 'white', borderRadius: '8px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              )
            }}
          />
          <TextField
            select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            size="small"
            label="Ordina per"
            sx={{ minWidth: '160px', backgroundColor: 'white', borderRadius: '8px' }}
          >
            <MenuItem value="data">Data registrazione</MenuItem>
            <MenuItem value="interazioni">Interazioni</MenuItem>
          </TextField>
        </Box>

        {/* Customer List */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredCustomers.length === 0 ? (
          <Paper elevation={1} sx={{ p: 4, textAlign: 'center', borderRadius: '16px' }}>
            <PeopleIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Nessun cliente trovato
            </Typography>
            <Typography variant="body2" color="text.secondary">
              I contatti dei clienti appariranno qui quando useranno ProvaMi
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {filteredCustomers.map(customer => (
              <Card
                key={customer.hash}
                elevation={1}
                sx={{
                  borderRadius: '12px',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: 4 }
                }}
              >
                <CardContent
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:last-child': { pb: 2 }
                  }}
                  onClick={() => setExpandedCustomer(
                    expandedCustomer === customer.hash ? null : customer.hash
                  )}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: customer.type === 'email' ? '#E3F2FD' : '#FFF3E0',
                      flexShrink: 0
                    }}
                  >
                    {customer.type === 'email'
                      ? <EmailIcon sx={{ color: '#2196F3' }} />
                      : <PhoneIcon sx={{ color: '#FF9800' }} />
                    }
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '15px' }} noWrap>
                      {customer.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Registrato: {formatDate(customer.createdAt)}
                      {customer.lastSeen && customer.lastSeen !== customer.createdAt && (
                        <> | Ultimo accesso: {formatDate(customer.lastSeen)}</>
                      )}
                    </Typography>
                  </Box>

                  <Chip
                    label={`${customer.interactionCount} interazioni`}
                    size="small"
                    sx={{
                      backgroundColor: customer.interactionCount > 0 ? '#E8F5E9' : '#f5f5f5',
                      color: customer.interactionCount > 0 ? '#2E7D32' : '#999',
                      fontWeight: 600,
                      flexShrink: 0
                    }}
                  />

                  <IconButton size="small">
                    {expandedCustomer === customer.hash
                      ? <ExpandLessIcon />
                      : <ExpandMoreIcon />
                    }
                  </IconButton>
                </CardContent>

                <Collapse in={expandedCustomer === customer.hash}>
                  <Box sx={{ px: 2, pb: 2 }}>
                    {customer.interactions.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ pl: 7 }}>
                        Nessuna interazione registrata
                      </Typography>
                    ) : (
                      <List dense sx={{ pl: 5 }}>
                        {customer.interactions.map((interaction, idx) => (
                          <ListItem key={idx} sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              {getInteractionIcon(interaction.type)}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {getInteractionLabel(interaction.type)}: {interaction.productName}
                                </Typography>
                              }
                              secondary={
                                `${formatDate(interaction.timestamp)} ${formatTime(interaction.timestamp)}`
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </Box>
                </Collapse>
              </Card>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
}

export default AdminCustomers;
